import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, LogIn, UserPlus, Newspaper, TrendingUp, CalendarDays, Heart, Loader2, Layers, Sparkles, Globe, FileText, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HeroCarousel } from "@/components/HeroCarousel";
import { DateFeteBar } from "@/components/DateFeteBar";
import { SlotAd } from "@/components/SlotAd";
import { AmbassadorChallengeHome } from "@/components/AmbassadorChallengeHome";
import { EditionCard } from "@/components/EditionCard";
import { useReader } from "@/components/Layout";
import { SearchDropdown } from "@/components/SearchDropdown";
import { AMBASSADOR_VISIBLE } from "@/lib/challenge";
import { NewIssueBanner } from "@/components/NewIssueBanner";
import { api } from "@/lib/appresse";
import { brandOf, brandKey, coverThumbUrl, pageUrl, getToken } from "@/lib/appresse";
import { catIcon } from "@/lib/categoryIcons";
import { useAutoScrollX } from "@/lib/useAutoScrollX";
import { useAuth } from "@/lib/auth";

const Section = ({ title, icon: Icon, editions, to, auto = false, compact = false, reverse = false, limit = 24 }) => {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (!auto) return;
    const el = scrollRef.current;
    if (!el) return;
    let raf, paused = false, resumeT;
    const SPEED = 0.4; // px/frame ≈ very slow
    let pos = reverse ? el.scrollWidth / 2 : el.scrollLeft;
    el.scrollLeft = pos;
    const step = () => {
      if (!paused && el.scrollWidth > el.clientWidth) {
        const half = el.scrollWidth / 2;
        if (reverse) { pos -= SPEED; if (pos <= 0) pos += half; }  // visually left → right
        else { pos += SPEED; if (pos >= half) pos -= half; }        // right → left
        el.scrollLeft = pos;
      }
      raf = requestAnimationFrame(step);
    };
    const pause = () => { paused = true; clearTimeout(resumeT); };
    const resume = () => { clearTimeout(resumeT); resumeT = setTimeout(() => { pos = el.scrollLeft; paused = false; }, 2500); };
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resume, { passive: true });
    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("wheel", () => { pause(); resume(); }, { passive: true });
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf); clearTimeout(resumeT);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resume);
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
    };
  }, [auto, reverse, editions.length]);

  if (!editions.length) return null;
  // Cap how many cards actually mount so the first paint stays instant even on
  // old phones (iPhone 7 / iOS 15) — the full list is one tap away via "Voir tout".
  const base = editions.slice(0, limit);
  const items = auto ? [...base, ...base] : base;
  const cardW = compact ? "w-40 sm:w-44" : "w-[44%] sm:w-52";
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif font-black text-2xl flex items-center gap-2">
          <Icon className="w-6 h-6 text-primary" /> {title}
        </h2>
        <Link to={to} className="text-sm font-medium text-primary inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`voir-tout-${title}`}>
          Voir tout <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div ref={scrollRef} className={`flex gap-4 sm:gap-5 overflow-x-auto thin-scroll pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 ${auto ? "" : "snap-x"}`}>
        {items.map((e, i) => (
          <div key={auto ? `${e.id}-${i}` : e.id} className={`shrink-0 ${cardW} ${auto ? "" : "snap-start"}`}>
            <EditionCard edition={e} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
};


// (auto-scroll hook moved to @/lib/useAutoScrollX and imported at top)

// Persist the home payload so the app paints instantly on the next open (PWA
// cold start, refresh, back-navigation) with all its visuals, then quietly
// revalidates in the background. Works the same on phones, tablets & desktops.
const HOME_LS_KEY = "appresse_home_cache_v2";
function readHomeCache() {
  try {
    const r = JSON.parse(localStorage.getItem(HOME_LS_KEY));
    if (r && Array.isArray(r.all)) return { all: r.all, featured: r.featured || [], cats: r.cats || [] };
  } catch { /* ignore */ }
  return { all: [], featured: [], cats: [] };
}
function writeHomeCache(c) {
  try { localStorage.setItem(HOME_LS_KEY, JSON.stringify(c)); } catch { /* quota */ }
}

let HOME_CACHE = readHomeCache();
let HOME_LAST_LOAD = 0;


export default function Home() {
  const { user, favorites, toggleFavorite } = useAuth();
  const navigate = useNavigate();
  const [all, setAll] = useState(HOME_CACHE.all);
  const [featured, setFeatured] = useState(HOME_CACHE.featured);
  const [cats, setCats] = useState(HOME_CACHE.cats);
  const cancelledRef = useRef(false);
  const versionRef = useRef(null);
  const [newOnes, setNewOnes] = useState([]);
  const closeNew = useCallback(() => setNewOnes([]), []);
  const catsRef = useRef(null);
  useAutoScrollX(catsRef, cats.length > 0);

  const load = useCallback(async (attempt = 0) => {
    try {
      const h = await api.home();
      if (cancelledRef.current) return true;
      const eds = h.editions || [];
      const feat = h.featured && h.featured.length ? h.featured : eds;
      const cc = (h.categories || []).filter((x) => x.count > 0);
      const known = new Set(HOME_CACHE.all.map((x) => x.id));
      const fresh = HOME_CACHE.all.length ? eds.slice(0, 30).filter((x) => !known.has(x.id)) : [];
      if (fresh.length) setNewOnes(fresh);
      versionRef.current = h.version;
      if (getToken()) {
        const hq = window.matchMedia?.("(pointer: coarse)").matches ? (Math.min(window.screen.width, window.screen.height) < 600 ? "p" : "h") : "d";
        const warm = () => feat.slice(0, 4).forEach((x) => { if (x.pages) new Image().src = `${pageUrl(x.id, 1)}&q=${hq}`; });
        (window.requestIdleCallback || ((f) => setTimeout(f, 1500)))(warm);
      }
      setAll(eds); setFeatured(feat); setCats(cc);
      HOME_CACHE = { all: eds, featured: feat, cats: cc };
      writeHomeCache(HOME_CACHE);
      HOME_LAST_LOAD = Date.now();
      return true;
    } catch (err) {
      if (!cancelledRef.current && attempt < 4) {
        setTimeout(() => load(attempt + 1), 800 * (attempt + 1));
      }
      return false;
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    const onVisible = () => {
      // The user wants the latest news on every app open / foreground → always refetch.
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const poll = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      api.homeVersion().then((v) => { if (versionRef.current && v.version !== versionRef.current) load(); }).catch(() => {});
    }, 15000);
    return () => {
      clearInterval(poll);
      cancelledRef.current = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [load]);

  // ---- Pull-to-refresh (mobile) — passive & DOM-driven so it never janks scroll ----
  const [refreshing, setRefreshing] = useState(false);
  const ptrEl = useRef(null);
  const ptrIcon = useRef(null);
  const ptr = useRef({ startY: 0, active: false, dist: 0, busy: false });
  const THRESHOLD = 70;
  const MAX_PULL = 110;

  useEffect(() => {
    const el = () => ptrEl.current;
    const paint = (dist) => {
      const node = el();
      if (!node) return;
      node.style.opacity = String(Math.min(dist / THRESHOLD, 1));
      node.style.transform = `translateY(${Math.max(dist - 20, 0)}px)`;
      if (ptrIcon.current) ptrIcon.current.style.transform = `rotate(${dist * 3}deg)`;
    };
    const reset = () => {
      const node = el();
      if (node) { node.style.opacity = "0"; node.style.transform = "translateY(0)"; }
      ptr.current.dist = 0;
    };
    const onStart = (e) => {
      if (ptr.current.busy) return;
      if (window.scrollY <= 0) {
        ptr.current.startY = e.touches[0].clientY;
        ptr.current.active = true;
      } else {
        ptr.current.active = false;
      }
    };
    const onMove = (e) => {
      if (!ptr.current.active || ptr.current.busy) return;
      const dy = e.touches[0].clientY - ptr.current.startY;
      if (dy > 0 && window.scrollY <= 0) {
        const dist = Math.min(dy * 0.5, MAX_PULL);
        ptr.current.dist = dist;
        paint(dist);
      } else {
        ptr.current.active = false;
        reset();
      }
    };
    const onEnd = async () => {
      if (!ptr.current.active) return;
      ptr.current.active = false;
      if (ptr.current.dist >= THRESHOLD && !ptr.current.busy) {
        ptr.current.busy = true;
        paint(THRESHOLD);
        setRefreshing(true);
        await load();
        setRefreshing(false);
        ptr.current.busy = false;
        reset();
      } else {
        reset();
      }
    };
    // overscroll-behavior stops the browser's own pull-to-refresh so ours is clean
    const prevOB = document.body.style.overscrollBehaviorY;
    document.body.style.overscrollBehaviorY = "contain";
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.body.style.overscrollBehaviorY = prevOB;
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  // "Aujourd'hui" = everything PUT today (upload date) or dated today — so every
  // journal added today shows up and the list renews each day.
  const isToday = (e) => ((e.created_at || "").slice(0, 10) === today) || (e.publication_date === today);
  const auj = all.filter(isToday);
  const aujBase = auj.length ? auj : all.slice(4, 8);
  // French revues stay in "Aujourd'hui"; other languages go to the world section.
  // Order: normal first, then local/regional press ("Régions"), then any edition the
  // admin explicitly forced to the end (show_last) — always last / far right.
  const rank = (e) => (e.show_last ? 2 : (e.category === "Régions" ? 1 : 0));
  const aujourdhui = aujBase
    .filter((e) => !e.international && e.category !== "Régions")
    .slice()
    .sort((a, b) => rank(a) - rank(b));
  const aujMonde = all.filter((e) => e.international);
  // Presse régionale : section fixe dédiée (toutes les éditions de la rubrique
  // « Régions »), affichée au-dessus de la presse internationale.
  const aujRegion = all.filter((e) => e.category === "Régions" && !e.international);

  // One tile per publication brand (latest issue as its cover). Editions arrive
  // sorted by date desc, so the first seen per brand is the most recent. Grouping
  // uses the normalized brandKey so spelling variants collapse into one tile.
  const brands = useMemo(() => {
    const map = new Map();
    all.forEach((e) => {
      const k = brandKey(e.title);
      if (!map.has(k)) map.set(k, { key: k, name: brandOf(e.title), ed: e });
    });
    return [...map.values()];
  }, [all]);

  // Brand keys the user has favorited (any issue of that brand counts).
  const favBrandKeys = useMemo(() => {
    const s = new Set();
    all.forEach((e) => { if (favorites?.has(e.id)) s.add(brandKey(e.title)); });
    return s;
  }, [all, favorites]);
  const myBrands = brands.filter((b) => favBrandKeys.has(b.key));
  const showMine = Boolean(user) && myBrands.length > 0;
  const shownBrands = (showMine ? myBrands : brands).slice(0, 24);
  const moreBrands = (showMine ? myBrands : brands).length > 24;
  // Most recent publication date among the shown brands → its edition(s) get a
  // "Nouveau" badge and sit first, so the latest release shows up front.
  const newestBrandDate = useMemo(
    () => shownBrands.reduce((m, b) => (b.ed.publication_date > m ? b.ed.publication_date : m), ""),
    [shownBrands]
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5">
      <NewIssueBanner items={newOnes} onClose={closeNew} />
      <div
        ref={ptrEl}
        className="fixed top-0 left-0 right-0 z-40 flex justify-center pointer-events-none"
        style={{ opacity: 0, transform: "translateY(0)", willChange: "opacity, transform" }}
        data-testid="ptr-indicator"
      >
        <div className="w-10 h-10 rounded-full bg-card border border-border shadow-[0_6px_18px_-4px_rgba(0,0,0,0.4)] flex items-center justify-center mt-2">
          <Loader2 ref={ptrIcon} className={`w-5 h-5 text-primary ${refreshing ? "animate-spin" : ""}`} />
        </div>
      </div>
      <DateFeteBar />

      <SearchDropdown />

      {AMBASSADOR_VISIBLE && <AmbassadorChallengeHome />}

      {!user && (
        <div className="grid grid-cols-2 gap-2 mt-4" data-testid="home-cta-row">
          <Button variant="outline" className="gap-1 text-xs px-1 shadow-[0_6px_16px_-4px_rgba(0,0,0,0.4)] hover:shadow-[0_10px_22px_-6px_rgba(0,0,0,0.5)] transition-shadow" onClick={() => navigate("/compte")} data-testid="cta-login">
            <LogIn className="w-4 h-4 shrink-0" /> Mon espace
          </Button>
          <Button className="gap-1 text-xs px-1 shadow-[0_6px_16px_-4px_rgba(0,0,0,0.4)] hover:shadow-[0_10px_22px_-6px_rgba(0,0,0,0.5)] transition-shadow" onClick={() => navigate("/compte?tab=register")} data-testid="cta-register">
            <UserPlus className="w-4 h-4 shrink-0" /> <span className="truncate">Créer mon espace</span>
          </Button>
        </div>
      )}

      <div className="mt-5">
        <HeroCarousel />
      </div>

      <SlotAd
        slot="home_1"
        className="mt-6"
      />

      <Section title="À la une" icon={TrendingUp} editions={featured} to="/explorer" auto />
      <Section title="Aujourd'hui" icon={CalendarDays} editions={aujourdhui} to="/explorer?today=1" limit={100} />
      <Section title="Presse régionale" icon={MapPin} editions={aujRegion} to="/explorer?cat=R%C3%A9gions" auto reverse limit={100} />
      <Section title="Presse internationale" icon={Globe} editions={aujMonde} to="/explorer?international=1" auto compact />

      <section className="mt-10">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-serif font-black text-2xl">Mes journaux favoris</h2>
            {user && !showMine && (
              <p className="text-sm text-muted-foreground mt-1">
                Touchez le <Heart className="inline w-3.5 h-3.5 -mt-0.5 text-red-500" /> sur un journal pour l'ajouter ici
              </p>
            )}
            <p className="text-sm text-muted-foreground mt-1">
              Touchez le bouton sous un journal pour retrouver toutes ses éditions.
            </p>
          </div>
          {moreBrands && (
            <Link to="/explorer" className="shrink-0 text-sm font-medium text-primary inline-flex items-center gap-1 hover:gap-2 transition-all mt-1" data-testid="voir-tout-brands">
              Voir tout <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        <div className="flex gap-4 overflow-x-auto thin-scroll pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 snap-x">
          {shownBrands.map(({ key, name, ed }) => {
            const isFav = favBrandKeys.has(key);
            const isNew = Boolean(newestBrandDate) && ed.publication_date === newestBrandDate;
            return (
              <div key={name} className="shrink-0 w-28 sm:w-32 snap-start relative group text-left">
                <button
                  onClick={() => navigate(`/explorer?brand=${encodeURIComponent(name)}`)}
                  data-testid={`home-brand-${name}`}
                  className="block w-full"
                  aria-label={`Voir ${name}`}
                >
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-border bg-muted shadow-[0_8px_22px_-6px_rgba(0,0,0,0.38)] transition-shadow duration-300 group-hover:shadow-[0_14px_34px_-8px_rgba(0,0,0,0.55)] group-hover:border-primary/50 relative">
                    {isNew && (
                      <span
                        data-testid={`home-brand-new-${name}`}
                        className="absolute bottom-1.5 left-1.5 z-10 inline-flex items-center gap-0.5 rounded-full bg-red-500/90 backdrop-blur-sm text-white text-[8px] font-bold uppercase tracking-wide px-1.5 py-[1px] shadow-sm"
                      >
                        <Sparkles className="w-2 h-2" /> Nouveau
                      </span>
                    )}
                    {ed.has_cover ? (
                      <>
                        {ed.thumb_blur && (
                          <img src={ed.thumb_blur} alt="" aria-hidden
                            className="absolute inset-0 w-full h-full object-cover object-top scale-110 blur-lg" />
                        )}
                        <img src={coverThumbUrl(ed.id)} alt={name} loading="lazy" decoding="async"
                          onLoad={(e) => { const b = e.currentTarget.previousElementSibling; if (b) b.style.display = "none"; }}
                          className="relative w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.04]" />
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center paper-texture">
                        <Newspaper className="w-8 h-8 text-primary" />
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs font-semibold leading-tight line-clamp-2 min-h-[1.9rem]">{name}</p>
                </button>
                <div className="relative group/ed mt-1">
                  <button
                    type="button"
                    onClick={() => navigate(`/explorer?brand=${encodeURIComponent(name)}`)}
                    data-testid={`home-brand-archive-${name}`}
                    className="w-full flex items-center justify-center gap-1.5 rounded-full bg-[#0A2472]/10 text-[#0A2472] text-[11px] font-semibold py-1.5 px-2 transition-colors hover:bg-[#0A2472] hover:text-white"
                  >
                    {ed.has_cover ? (
                      <img src={coverThumbUrl(ed.id)} alt="" aria-hidden
                        className="w-9 h-9 rounded-[3px] object-cover object-top shrink-0 bg-white shadow-sm" />
                    ) : (
                      <Layers className="w-7 h-7 shrink-0" />
                    )}
                    <span>Toutes les éditions</span>
                  </button>
                  {ed.has_cover && (
                    <div className="hidden sm:block pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-30 opacity-0 scale-90 origin-bottom transition-all duration-200 group-hover/ed:opacity-100 group-hover/ed:scale-100">
                      <img src={coverThumbUrl(ed.id)} alt={name}
                        className="w-44 rounded-lg border border-border bg-white shadow-[0_18px_44px_-10px_rgba(0,0,0,0.6)] object-cover object-top" />
                    </div>
                  )}
                </div>
                {user && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(ed.id, ed.title); }}
                    data-testid={`home-brand-fav-${name}`}
                    aria-pressed={isFav}
                    aria-label={isFav ? `Retirer ${name} de mes journaux` : `Ajouter ${name} à mes journaux`}
                    className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full flex items-center justify-center bg-black/35 backdrop-blur-sm text-white shadow-sm transition-all hover:bg-black/55 active:scale-90"
                  >
                    <Heart className={`w-4 h-4 transition-all ${isFav ? "fill-red-500 text-red-500 scale-110" : "text-white"}`} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-serif font-black text-2xl mb-4">Nos catégories</h2>
        <div ref={catsRef} className="flex gap-3 overflow-x-auto thin-scroll pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6">
          {(cats.length > 1 ? [...cats, ...cats] : cats).map((c, i) => {
            const Icon = catIcon(c.name);
            const active = (i % (cats.length || 1)) === 0;
            return (
              <button
                key={`${c.name}-${i}`}
                onClick={() => navigate(`/explorer?cat=${encodeURIComponent(c.name)}`)}
                data-testid={`home-cat-${c.name}`}
                className={`shrink-0 inline-flex items-center gap-2.5 pl-3.5 pr-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all shadow-[0_6px_16px_-4px_rgba(0,0,0,0.4)] hover:shadow-[0_10px_22px_-6px_rgba(0,0,0,0.5)] ${
                  active
                    ? "bg-[#2563FF] text-white"
                    : "bg-secondary text-foreground hover:bg-muted"
                }`}
              >
                <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={2} />
                {c.name}
              </button>
            );
          })}
        </div>
      </section>

      {brands.length > 0 && (
        <section className="mt-10" data-testid="home-choose-revue">
          <button
            onClick={() => navigate("/explorer")}
            data-testid="home-choose-revue-link"
            className="group w-full text-left rounded-3xl bg-gradient-to-br from-[#0A2472] to-[#123499] text-white p-6 sm:p-7 shadow-[0_14px_34px_-10px_rgba(10,36,114,0.6)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_44px_-12px_rgba(10,36,114,0.7)]"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h2 className="font-serif font-black text-2xl sm:text-3xl">Je choisis ma revue</h2>
                <p className="text-sm text-white/80 mt-2 leading-relaxed">
                  Parcourez tous les titres. Touchez le
                  <Heart className="inline w-4 h-4 mx-1 -mt-0.5 fill-red-500 text-red-500" />
                  d'une revue pour l'ajouter à vos favoris — vous retrouverez ensuite
                  l'intégralité de vos journaux préférés réunis en bas de l'accueil.
                </p>
              </div>
              <ArrowRight className="w-7 h-7 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
            </div>
          </button>
        </section>
      )}

      <SlotAd
        slot="home_2"
        className="mt-10"
      />
    </div>
  );
}
